import { useQuery } from "@apollo/client/react";
import { queries } from "@/graphql";
import type {
  FbPartnerType,
  FbUserType,
  QueryPartnersUsersListArgs,
} from "@/types/graphql";

type PartnerUsersListItem = FbUserType & {
  companyId?: string | null;
  partner?: FbPartnerType | null;
};

type PartnerUsersListQueryData = {
  partnersUsersList: Array<PartnerUsersListItem | null> | null;
};

const toDateOrNull = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === "string" || typeof value === "number") {
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  return null;
};

/**
 * Custom hook to fetch and format the partner users list.
 * @param {string} partnerId - The ID of the partner whose users are to be fetched.
 * @returns {{usersList: Array<{id: string, name: string, email: string, countryCode: string, timezone: string, roles: Array<string>, lang: string, companyId: string, partner: any, partnerName: string}>, loading: boolean}} - The formatted partner users list and loading state.
 */
export function usePartnerUsersList(partnerId: string) {
  const skip = !partnerId;

  const { data, loading } = useQuery<
    PartnerUsersListQueryData,
    QueryPartnersUsersListArgs
  >(queries.getPartnerUsersList, {
    variables: {
      partnerId,
    },
    skip,
  });

  // Extracting the necessary data and formatting it as needed
  const formattedData =
    data?.partnersUsersList
      ?.filter((user): user is PartnerUsersListItem => Boolean(user))
      .map((user) => ({
        id: user.id,
        name: user.name,
        email: user.email,
        countryCode: user.countryCode,
        timezone: user.timezone,
        roles: user.roles,
        lang: user.lang,
        companyId: user.companyId,
        partner: user.partner,
        status: user.status,
        isVerified: Boolean(user.isVerified),
        lastLogin: toDateOrNull(user.lastLogin),
        createdAt: user.createdAt,
        updatedAt: user.updatedAt,
        partnerName: user.partner ? user.partner.name : "N/A",
      })) || [];

  return {
    usersList: formattedData,
    loading,
  };
}
